import { ReactNode, createContext, useEffect, useMemo, useState } from 'react';
import { getGms2SelectedOrgStorageKey } from 'constants/storageKeys';
import { useUser } from 'queries/UseUsers';
import { IOrganizationInfo } from 'services/types/organizationTypes';
import { IOrganization, IUser } from 'services/types/userTypes';
import { IAppContext } from './types/AppProviderTypes';

const getAuth0SubFromSession = (): string | null => {
  try {
    const raw = window.sessionStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw) as { sub?: string };
      return parsed?.sub ?? null;
    }
  } catch {
    // ignore
  }
  return null;
};

type ProviderProps = {
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  envConfig: Record<string, any>;
};

export const AppContext = createContext<IAppContext>({
  selectedGivingFormId: '',
  setSelectedGivingFormId: () => ({}),
  selectedTemplateId: '',
  setSelectedTemplateId: () => ({}),
  selectedCampaignName: '',
  setSelectedCampaignName: () => ({}),
  selectedCampaignId: '',
  setSelectedCampaignId: () => ({}),
  showNewCampaignDialog: false,
  setShowNewCampaignDialog: () => ({}),
  selectedOrganization: {},
  setSelectedOrganization: () => ({}),
  organizationInfo: {},
  setOrganizationInfo: () => ({}),
  userOrganizations: [],
  setUserOrganizations: () => [],
  abTestName: '',
  setNewAbTestName: () => ({}),
  envConfig: {}
});

const pendoConfig = (user: IUser, org: IOrganization) => ({
  visitor: {
    id: user.id,
    email: user.email,
    full_name: `${user.email}`
  },
  account: {
    id: org.id,
    name: org.name
  }
});

const storeSelectedOrg = (user: IUser, organization: IOrganization) => {
  // Store currently selected organization keyed off logged in user's id.
  if (user?.id) {
    window.localStorage.setItem(
      `${user.id}_selectedOrganization`,
      organization.id
    );
  }
  // Same key format as point-o-gms (useDefaultOrg) so they can read it on load
  const auth0Sub = getAuth0SubFromSession();
  if (auth0Sub) {
    window.localStorage.setItem(
      getGms2SelectedOrgStorageKey(auth0Sub),
      organization.id
    );
  }
};

const getSelectedOrganizationFromStorage = (user: IUser) => {
  // Prefer shared key (gms-selected-org-${auth0Sub}) so point-o org changes reflect in gms2
  const auth0Sub = getAuth0SubFromSession();
  const orgId =
    (auth0Sub &&
      window.localStorage.getItem(getGms2SelectedOrgStorageKey(auth0Sub))) ||
    window.localStorage.getItem(`${user?.id}_selectedOrganization`);
  if (orgId && user?.organizations) {
    const org = user.organizations.find((o: IOrganization) => o.id === orgId);
    if (org) return org;
  }
  if (user?.organizations?.length) {
    storeSelectedOrg(user, user.organizations[0]);
    return user.organizations[0];
  }
  return null;
};

export const AppProvider = ({ children, envConfig }: ProviderProps) => {
  const { data: user } = useUser();

  const [selectedGivingFormId, setSelectedGivingFormId] = useState<string>('');
  const [selectedCampaignName, setSelectedCampaignName] = useState<string>('');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [showNewCampaignDialog, setShowNewCampaignDialog] =
    useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedOrganization, _setSelectedOrganization] =
    useState<IOrganization>(null);
  const [organizationInfo, setOrganizationInfo] = useState<IOrganizationInfo>(
    {}
  );
  const [userOrganizations, setUserOrganizations] = useState<IOrganization[]>(
    []
  );
  const [abTestName, setNewAbTestName] = useState<string>('');

  // useUser is asyncronous, so we need to wait for it to return before
  // attempting to set selected and available organizations in state
  useEffect(() => {
    if (user && !userOrganizations.length) {
      setUserOrganizations(user.organizations);
      _setSelectedOrganization(getSelectedOrganizationFromStorage(user));
    }
  }, [user, userOrganizations]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { pendo } = window as any;

    if (user && selectedOrganization) {
      if (!pendo.accountId) {
        pendo.initialize(pendoConfig(user, selectedOrganization));
      } else {
        pendo.updateOptions(pendoConfig(user, selectedOrganization));
      }
    }
  }, [user, selectedOrganization]);

  const context = useMemo(
    () => ({
      selectedGivingFormId,
      setSelectedGivingFormId,
      selectedCampaignName,
      showNewCampaignDialog,
      setShowNewCampaignDialog,
      selectedCampaignId,
      setSelectedCampaignId,
      setSelectedCampaignName,
      selectedTemplateId,
      setSelectedTemplateId,
      selectedOrganization,
      setSelectedOrganization: (organization: IOrganization) => {
        // When we update selected org, make sure to update it in storage too
        storeSelectedOrg(user, organization);
        _setSelectedOrganization(organization);
      },
      organizationInfo,
      setOrganizationInfo,
      userOrganizations,
      setUserOrganizations,
      abTestName,
      setNewAbTestName,
      envConfig
    }),
    [
      selectedGivingFormId,
      setSelectedGivingFormId,
      selectedCampaignName,
      setSelectedCampaignName,
      showNewCampaignDialog,
      setShowNewCampaignDialog,
      selectedTemplateId,
      setSelectedTemplateId,
      selectedOrganization,
      _setSelectedOrganization,
      organizationInfo,
      setOrganizationInfo,
      userOrganizations,
      setUserOrganizations,
      abTestName,
      setNewAbTestName,
      envConfig,
      user
    ]
  );
  return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
};
